/**
 * The input.
 *
 * Enter sends, Shift+Enter breaks a line. The textarea grows with the question,
 * because physics questions are often three paragraphs and a boundary
 * condition, and a one-line box discourages asking properly.
 *
 * Below it sits one visible decision — how long an answer — and a gear holding
 * the rest. Anything switched on behind that gear shows as a chip, so the quiet
 * row never hides what the answer is about to cost.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { ArrowUp, Square } from "lucide-react";
import type { DepthChoice } from "../../../shared/depth.ts";
import type { SendSettings } from "../../state/useChat.ts";
import { isRefinable, type Refinement } from "../../../shared/refine.ts";
import { Button } from "../ui/button.tsx";
import { Textarea } from "../ui/textarea.tsx";
import { Tooltip, TooltipContent, TooltipTrigger } from "../ui/tooltip.tsx";
import { LengthMenu } from "./LengthMenu.tsx";
import { RefineButton } from "./RefineButton.tsx";
import { RefineNote } from "./RefineNote.tsx";
import { SettingsPopover } from "./SettingsPopover.tsx";
import { SettingChips } from "./SettingChips.tsx";

interface ComposerProps {
  readonly onSend: (content: string, settings: SendSettings) => void;
  readonly onStop: () => void;
  readonly busy: boolean;
  readonly settings: SendSettings;
  readonly onSettingsChange: (settings: SendSettings) => void;
  /** What the chips compare against to decide what counts as an override. */
  readonly defaultSettings: SendSettings;
  /**
   * Injected rather than imported so this component still owns no network:
   * the composer decides what happens to the reader's text, not how a
   * rewrite is fetched.
   */
  readonly onRefine: (content: string) => Promise<Refinement>;
}

/**
 * What a refinement left behind.
 *
 * `original` is present only when the text actually changed — an unchanged
 * question has nothing to revert *to*, and offering the button anyway would
 * imply something happened that didn't.
 */
interface RefineState {
  readonly note: string;
  readonly original: string | null;
}

const MAX_HEIGHT = 320;

export function Composer({
  onSend,
  onStop,
  busy,
  settings,
  onSettingsChange,
  defaultSettings,
  onRefine,
}: ComposerProps) {
  const [value, setValue] = useState("");
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [refining, setRefining] = useState(false);
  const [refined, setRefined] = useState<RefineState | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Grow to fit, then scroll — measured from scrollHeight after a reset.
  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    textarea.style.height = "auto";
    textarea.style.height = `${Math.min(textarea.scrollHeight, MAX_HEIGHT)}px`;
  }, [value]);

  const submit = useCallback(() => {
    if (busy || value.trim().length === 0) return;
    onSend(value, settings);
    setValue("");
    setRefined(null);
  }, [busy, onSend, settings, value]);

  /**
   * Put the caret back where the reader left it — at the end of their text.
   *
   * Swapping a textarea's value without touching focus leaves the caret at
   * index zero on the next click, so the natural "now let me tweak that"
   * gesture starts by typing into the middle of the first word.
   */
  const focusEnd = useCallback((text: string) => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    textarea.focus();
    textarea.setSelectionRange(text.length, text.length);
  }, []);

  const refine = useCallback(async () => {
    const current = value.trim();
    if (busy || refining || !isRefinable(current)) return;

    setRefining(true);
    try {
      const result = await onRefine(current);

      if (result.changed) {
        setValue(result.refined);
        setRefined({ note: result.note, original: current });
        focusEnd(result.refined);
        return;
      }

      // Saying "already specific" is a real answer, and a more useful one than
      // silently doing nothing — which reads as a broken button.
      setRefined({
        note: result.note || "That's already specific enough to send.",
        original: null,
      });
    } catch {
      // The question is still sitting in the box, untouched. Losing a
      // refinement costs the reader nothing, so it is not worth an alert.
      setRefined(null);
    } finally {
      setRefining(false);
    }
  }, [busy, focusEnd, onRefine, refining, value]);

  const revert = useCallback(() => {
    const original = refined?.original;
    if (original === null || original === undefined) return;
    setValue(original);
    setRefined(null);
    focusEnd(original);
  }, [focusEnd, refined]);

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (event.key === "Enter" && !event.shiftKey) {
        event.preventDefault();
        submit();
      }
    },
    [submit],
  );

  return (
    <div className="flex flex-col gap-2">
      {refined && (
        <RefineNote
          note={refined.note}
          onRevert={refined.original === null ? undefined : revert}
        />
      )}

      {/* `border-control` rather than `border-strong`: this is the boundary that
          identifies the app's primary input, so it is held to 3:1 (WCAG 1.4.11)
          rather than left at the weight of a decorative hairline. */}
      <div className="flex items-end gap-2 rounded-xl border border-border-control bg-card p-2 shadow-sm transition-colors focus-within:border-ring">
        <Textarea
          ref={textareaRef}
          value={value}
          onChange={(event) => setValue(event.target.value)}
          onKeyDown={onKeyDown}
          placeholder="Ask anything — derive it, plot it, or explain why it works…"
          rows={1}
          aria-label="Your question"
          className="min-h-0 resize-none border-0 bg-transparent px-2 py-1.5 font-serif text-base shadow-none focus-visible:border-0 focus-visible:ring-0 dark:bg-transparent"
        />

        {busy ? (
          <Button
            variant="outline"
            size="icon"
            onClick={onStop}
            aria-label="Stop"
            className="rounded-full hover:border-destructive hover:text-destructive"
          >
            <Square className="fill-current" />
          </Button>
        ) : (
          <Button
            size="icon"
            onClick={submit}
            disabled={value.trim().length === 0}
            aria-label="Send"
            className="rounded-full"
          >
            <ArrowUp />
          </Button>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2 px-1 font-sans text-xs">
        <LengthMenu
          value={settings.depth}
          onChange={(depth: DepthChoice) => onSettingsChange({ ...settings, depth })}
        />

        <SettingsPopover
          settings={settings}
          onChange={onSettingsChange}
          open={settingsOpen}
          onOpenChange={setSettingsOpen}
        />

        <RefineButton
          onClick={() => void refine()}
          busy={refining}
          disabled={busy || !isRefinable(value)}
        />

        <SettingChips
          settings={settings}
          defaults={defaultSettings}
          onChange={onSettingsChange}
          onInspect={() => setSettingsOpen(true)}
        />

        {/* A keyboard hint is noise on a device with no keyboard, and on a
            phone it is the thing that wraps the settings row onto two lines. */}
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="ml-auto hidden cursor-default text-muted-foreground sm:inline">
              ⏎ send
            </span>
          </TooltipTrigger>
          <TooltipContent side="top">
            Enter to send · Shift+Enter for a new line
          </TooltipContent>
        </Tooltip>
      </div>
    </div>
  );
}
